// @ts-check
// =============================================================================
// js/core/router.js — the tiny hash router.
//
// WHAT THIS IS
//   Replaces the old client's `state = "lobby"` string + Write() switchboard
//   with real routes: #/lobby, #/deck/3, #/duel, #/gallery … Back/forward and
//   shareable deep links work for free (plan decision #10: hash over History
//   API — simpler, no server config).
//
// HOW IT WORKS
//   Screens register as { path, render(container, params), destroy?() }.
//   On every hashchange the router tears down the previous screen (calls its
//   destroy) and renders the new one into the app's screen container.
//
// PATHS
//   A route path is a static segment plus optional params: 'deck/:index'.
//   '#/' (or empty hash) resolves to the DEFAULT route ('lobby').
// =============================================================================

/** @typedef {{ path: string, render: (el: HTMLElement, params: Record<string,string>) => void, destroy?: () => void }} Route */

/** @type {Route[]} */
const routes = [];

/** The screen currently on stage (so we can destroy it before the next one). */
let activeRoute = /** @type {Route|null} */ (null);

/** Where screens render. Set once by startRouter(). */
let containerEl = /** @type {HTMLElement|null} */ (null);

/** Route used when the hash is empty or matches nothing. */
const DEFAULT_PATH = 'lobby';

/**
 * Register a screen with the router. Call once per screen at boot.
 * @param {Route} route
 * @returns {void}
 */
export function registerRoute(route) {
	routes.push(route);
}

/**
 * Navigate to a route programmatically, e.g. navigate('deck/3').
 * @param {string} path  Path WITHOUT the leading '#/' prefix.
 * @returns {void}
 */
export function navigate(path) {
	const target = '#/' + path;
	if (window.location.hash === target) {
		// Same hash -> hashchange won't fire; re-render explicitly (screens rely
		// on navigate() always producing a fresh render).
		renderCurrent();
	} else {
		window.location.hash = target;
	}
}

/**
 * The current path (without '#/'), e.g. "deck/3".
 * @returns {string}
 */
export function currentPath() {
	return window.location.hash.replace(/^#\/?/, '') || DEFAULT_PATH;
}

/**
 * Match a concrete path ("deck/3") against a route pattern ("deck/:index").
 * @param {string} pattern
 * @param {string} path
 * @returns {Record<string,string>|null}  Params if matched, else null.
 */
function matchPath(pattern, path) {
	const patternParts = pattern.split('/');
	const pathParts = path.split('/');
	if (patternParts.length !== pathParts.length) return null;
	/** @type {Record<string,string>} */
	const params = {};
	for (let i = 0; i < patternParts.length; i++) {
		if (patternParts[i].startsWith(':')) {
			params[patternParts[i].slice(1)] = decodeURIComponent(pathParts[i]);
		} else if (patternParts[i] !== pathParts[i]) {
			return null;
		}
	}
	return params;
}

/** Render whatever the current hash points at (fallback: default route). */
function renderCurrent() {
	if (!containerEl) return;
	const path = currentPath();

	let matched = null;
	let params = {};
	for (const route of routes) {
		const result = matchPath(route.path, path);
		if (result) {
			matched = route;
			params = result;
			break;
		}
	}
	if (!matched) {
		// Unknown hash -> go home rather than showing a blank page.
		navigate(DEFAULT_PATH);
		return;
	}

	// Tear down the outgoing screen first (stops its store subscriptions).
	if (activeRoute?.destroy) {
		try {
			activeRoute.destroy();
		} catch (error) {
			console.error(`destroy() of "${activeRoute.path}" threw:`, error);
		}
	}

	containerEl.innerHTML = '';
	activeRoute = matched;
	matched.render(containerEl, params);
}

/**
 * Start routing: point the router at the screen container and render the
 * current hash. Call once from boot AFTER login resolves.
 * @param {HTMLElement} container  Where screens render into.
 * @returns {void}
 */
export function startRouter(container) {
	containerEl = container;
	window.addEventListener('hashchange', renderCurrent);
	renderCurrent();
}
